import { Space, Tag, Tooltip, Typography } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { shortId } from '@/shared/lib/shortId'
import { useSiteIndex } from './useSiteIndex'

interface SiteRefProps {
  siteId: string | null
  /** Viloyat ham ko'rsatilsinmi — jadval ustunida ha, tor joyda yo'q */
  showRegion?: boolean
}

/**
 * Joylashuvga havola — id o'rniga NOM.
 *
 * ⚠️ ENG MUHIM QOIDA: bu komponent HECH QACHON bo'sh qaytarmaydi.
 *
 * Joylashuv biriktirilmagan qurilma — aynan shu funksiya oldini olmoqchi
 * bo'lgan nosozlik: uzilish haqida qo'ng'iroq kelganda "bu qurilma qayerda?"
 * degan savolga javob bo'lmasligi. Bo'sh katak esa buni YASHIRADI — u
 * "joylashuv yo'q" ni ham, "id bor, lekin bunday joylashuv topilmadi" ni ham
 * bir xil ko'rsatardi. Shuning uchun to'rt holat ham ALOHIDA chiziladi:
 *
 *   · `siteId === null`   → "ko'rsatilmagan" (ma'lumot sifati belgisi)
 *   · xarita yuklanmoqda  → "…" (hali xulosa chiqarilmaydi)
 *   · huquq yo'q / chala  → id, sababi tooltipda
 *   · topilmadi           → SARIQ ogohlantirish tegi
 */
export function SiteRef({ siteId, showRegion = true }: SiteRefProps) {
  const { t } = useTranslation()
  const { byId, loading, reliable } = useSiteIndex()

  if (siteId === null) {
    return (
      <Tooltip title={t('sites.unsetHint')}>
        <Typography.Text type="secondary" italic>
          {t('sites.unset')}
        </Typography.Text>
      </Tooltip>
    )
  }

  const site = byId.get(siteId)
  if (site) {
    return (
      <Space size={4} wrap>
        <Typography.Text>{site.name}</Typography.Text>
        {showRegion ? <Tag>{site.region}</Tag> : null}
        {site.state === 'CLOSED' ? <Tag color="default">{t('siteState.CLOSED')}</Tag> : null}
      </Space>
    )
  }

  if (loading) {
    return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
  }

  // Xarita ishonchli emas (huquq yo'q yoki chala) — id ko'rsatiladi, lekin
  // "noma'lum" deb AYBLANMAYDI: ma'lumot to'g'ri bo'lishi mumkin
  if (!reliable) {
    return (
      <Tooltip title={t('sites.indexUnavailable')}>
        <Typography.Text type="secondary" code>
          {/* Qisqartirish YAGONA joyda — `shared/lib/shortId.ts` (B12) */}
          {shortId(siteId)}
        </Typography.Text>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={t('sites.unknownHint')}>
      <Tag icon={<WarningOutlined />} color="warning">
        {t('sites.unknown')}
      </Tag>
    </Tooltip>
  )
}
